import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  bigint,
  boolean,
  jsonb,
  numeric,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants (project pattern: text columns + exported const arrays)
// ---------------------------------------------------------------------------

export const WEBINAR_TYPES = ["live", "evergreen", "hybrid"] as const;
export type WebinarType = (typeof WEBINAR_TYPES)[number];

export const WEBINAR_STATUSES = ["draft", "scheduled", "live", "ended", "cancelled"] as const;
export type WebinarStatus = (typeof WEBINAR_STATUSES)[number];

export const CTA_TYPES = ["link", "form", "offer", "poll"] as const;
export type CtaType = (typeof CTA_TYPES)[number];

export const RECORDING_FORMATS = ["mp4", "hls"] as const;
export type RecordingFormat = (typeof RECORDING_FORMATS)[number];

export const HIGHLIGHT_STATUSES = ["pending", "processing", "ready", "chapter", "failed"] as const;
export type HighlightStatus = (typeof HIGHLIGHT_STATUSES)[number];

export const CLIP_FORMATS = ["mp4", "vertical_mp4"] as const;
export type ClipFormat = (typeof CLIP_FORMATS)[number];

export const REGISTRATION_SOURCES = ["form", "api", "import", "workflow"] as const;
export type RegistrationSource = (typeof REGISTRATION_SOURCES)[number];

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface WebinarStreamConfig {
  hlsBucketPath?: string;
  egressId?: string;
  resolution?: string;
  framerate?: number;
  segmentDuration?: number;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// 1. Webinars
// ---------------------------------------------------------------------------

export const webinars = pgTable("webinars", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  title: text("title").notNull(),
  slug: text("slug").notNull(),
  description: text("description"),
  type: text("type").notNull(), // WebinarType
  timezone: text("timezone").notNull().default("UTC"),
  roomId: text("room_id"), // LiveKit room ID
  status: text("status").notNull().default("draft"), // WebinarStatus
  streamConfig: jsonb("stream_config").$type<WebinarStreamConfig>(),
  registrationEnabled: boolean("registration_enabled").notNull().default(true),
  registrationFormId: uuid("registration_form_id"),
  postWebinarWorkflowId: uuid("post_webinar_workflow_id"),
  scheduledAt: timestamp("scheduled_at", tz),
  startedAt: timestamp("started_at", tz),
  endedAt: timestamp("ended_at", tz),
  maxAttendees: integer("max_attendees"),
  peakConcurrentViewers: integer("peak_concurrent_viewers"), // Materialized post-webinar from ClickHouse
  // Series & scheduling extensions
  seriesId: uuid("series_id"),                       // FK to webinar_series (nullable)
  seriesOrder: integer("series_order"),               // order within series
  backstageOpenAt: timestamp("backstage_open_at", tz), // when backstage opens for presenters
  waitingRoomHtml: text("waiting_room_html"),          // custom waiting room content
  simulcastTargets: jsonb("simulcast_targets"),        // [{platform, rtmpUrl, streamKey}]
  breakoutConfig: jsonb("breakout_config"),            // {maxRooms, maxPerRoom, duration, assignmentMode}
  onDemand: boolean("on_demand").notNull().default(false),
  replayAvailableAt: timestamp("replay_available_at", tz),
  replayExpiresAt: timestamp("replay_expires_at", tz),
  reportGeneratedAt: timestamp("report_generated_at", tz),
  handouts: jsonb("handouts"),                         // [{name, r2Key, releasedAt}]
  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinars_tenant").on(table.tenantId),
  index("idx_webinars_status").on(table.tenantId, table.status),
  index("idx_webinars_scheduled").on(table.tenantId, table.scheduledAt),
  uniqueIndex("uq_webinars_tenant_slug").on(table.tenantId, table.slug),
]);

// ---------------------------------------------------------------------------
// 2. Webinar Registrations
// ---------------------------------------------------------------------------

export const webinarRegistrations = pgTable("webinar_registrations", {
  id: uuid("id").primaryKey().defaultRandom(),
  webinarId: uuid("webinar_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id").notNull(),
  registeredAt: timestamp("registered_at", tz).notNull().defaultNow(),
  source: text("source"), // RegistrationSource
  consentRecordId: uuid("consent_record_id"),
  reminderSentAt: timestamp("reminder_sent_at", tz),
  cancelledAt: timestamp("cancelled_at", tz),
  // CCW-021: per-registrant reminder opt-out (transactional, distinct from
  // list-level RFC 8058 marketing unsubscribe — a registrant who has opted
  // out of marketing email should still receive webinar reminders for a
  // webinar they explicitly registered for unless they ALSO flip this flag).
  reminderOptOut: boolean("reminder_opt_out").notNull().default(false),
  // CCW-021: SMS opt-in is a separate, explicit affirmative consent;
  // phoneE164 is null unless opt-in was captured.
  smsOptIn: boolean("sms_opt_in").notNull().default(false),
  phoneE164: text("phone_e164"),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_reg_tenant").on(table.tenantId),
  index("idx_webinar_reg_webinar").on(table.tenantId, table.webinarId),
  index("idx_webinar_reg_person").on(table.tenantId, table.personId),
  uniqueIndex("uq_webinar_reg_webinar_person").on(table.webinarId, table.personId),
]);

// ---------------------------------------------------------------------------
// CCW-021 Webinar Reminder Deliveries (per-registrant × per-step × per-channel
// ledger; UNIQUE(registration_id, step_id, channel) provides idempotency for
// `INSERT ... ON CONFLICT DO NOTHING` — re-running a workflow step cannot
// double-send).
// ---------------------------------------------------------------------------

export const WEBINAR_REMINDER_CHANNELS = ["email", "sms"] as const;
export type WebinarReminderChannel = (typeof WEBINAR_REMINDER_CHANNELS)[number];

export const WEBINAR_REMINDER_STATUSES = [
  "planned",
  "sent",
  "skipped-opt-out",
  "skipped-no-phone",
  "skipped-no-sms-opt-in",
  "skipped-provider-down",
  "bounced",
  "failed",
] as const;
export type WebinarReminderStatus =
  (typeof WEBINAR_REMINDER_STATUSES)[number];

export const webinarReminderDeliveries = pgTable("webinar_reminder_deliveries", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  registrationId: uuid("registration_id").notNull(),
  webinarId: uuid("webinar_id").notNull(),
  stepId: text("step_id").notNull(), // "pre.t-24h" | "pre.t-1h" | "pre.t-15m" | "post.followup.t+1h" | ...
  channel: text("channel").notNull(), // WebinarReminderChannel
  status: text("status").notNull(), // WebinarReminderStatus
  sentAt: timestamp("sent_at", tz),
  error: text("error"),
  dryRun: boolean("dry_run").notNull().default(false),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_reminder_deliv_tenant").on(table.tenantId),
  index("idx_webinar_reminder_deliv_registration").on(table.registrationId),
  index("idx_webinar_reminder_deliv_webinar").on(table.tenantId, table.webinarId),
  uniqueIndex("uq_webinar_reminder_delivery").on(
    table.registrationId,
    table.stepId,
    table.channel,
  ),
]);

// ---------------------------------------------------------------------------
// 3. Webinar Attendees
// ---------------------------------------------------------------------------

export const webinarAttendees = pgTable("webinar_attendees", {
  id: uuid("id").primaryKey().defaultRandom(),
  webinarId: uuid("webinar_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id").notNull(),
  joinedAt: timestamp("joined_at", tz).notNull(),
  leftAt: timestamp("left_at", tz),
  watchDurationSeconds: integer("watch_duration_seconds").notNull().default(0),
  engagementScore: integer("engagement_score"), // Nullable: null = not computed, 0 = zero engagement
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_att_tenant").on(table.tenantId),
  index("idx_webinar_att_webinar").on(table.tenantId, table.webinarId),
  index("idx_webinar_att_person").on(table.tenantId, table.personId),
  uniqueIndex("uq_webinar_att_webinar_person").on(table.webinarId, table.personId),
]);

// ---------------------------------------------------------------------------
// 4. Webinar CTA Overlays
// ---------------------------------------------------------------------------

export const webinarCtaOverlays = pgTable("webinar_cta_overlays", {
  id: uuid("id").primaryKey().defaultRandom(),
  webinarId: uuid("webinar_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  ctaType: text("cta_type").notNull(), // CtaType
  label: text("label").notNull(),
  url: text("url"),
  displayAtSeconds: integer("display_at_seconds"),
  durationSeconds: integer("duration_seconds"),
  // Materialized from ClickHouse webinar.cta_clicked events; atomic increment via SQL for real-time approximation
  clickCount: integer("click_count").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_cta_tenant").on(table.tenantId),
  index("idx_webinar_cta_webinar").on(table.tenantId, table.webinarId),
]);

// ---------------------------------------------------------------------------
// 5. Webinar Recordings
// ---------------------------------------------------------------------------

export const webinarRecordings = pgTable("webinar_recordings", {
  id: uuid("id").primaryKey().defaultRandom(),
  webinarId: uuid("webinar_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  r2Key: text("r2_key").notNull(),
  hlsPlaylistKey: text("hls_playlist_key"),
  durationSeconds: integer("duration_seconds"),
  format: text("format").notNull(), // RecordingFormat
  sizeBytes: bigint("size_bytes", { mode: "number" }), // bigint: recordings can exceed 2GB
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_rec_tenant").on(table.tenantId),
  index("idx_webinar_rec_webinar").on(table.tenantId, table.webinarId),
]);

// ---------------------------------------------------------------------------
// 6. Webinar Clips (AI-generated highlights)
// ---------------------------------------------------------------------------

export const webinarClips = pgTable("webinar_clips", {
  id: uuid("id").primaryKey().defaultRandom(),
  recordingId: uuid("recording_id").notNull(),
  webinarId: uuid("webinar_id").notNull(), // Denormalized for direct query without join
  tenantId: uuid("tenant_id").notNull(),
  title: text("title").notNull(),
  startMs: integer("start_ms").notNull(),
  endMs: integer("end_ms").notNull(),
  r2Key: text("r2_key"),
  captionText: text("caption_text"),
  generatedByAi: boolean("generated_by_ai").notNull().default(false), // EU AI Act disclosure
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_clip_tenant").on(table.tenantId),
  index("idx_webinar_clip_recording").on(table.tenantId, table.recordingId),
  index("idx_webinar_clip_webinar").on(table.tenantId, table.webinarId),
]);

// ---------------------------------------------------------------------------
// 7. Webinar Highlights (AI-detected engagement peaks)
// ---------------------------------------------------------------------------

export const webinarHighlights = pgTable("webinar_highlights", {
  id: uuid("id").primaryKey().defaultRandom(),
  webinarId: uuid("webinar_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  jobId: uuid("job_id"), // groups highlights from same generation run
  startSeconds: integer("start_seconds").notNull(),
  endSeconds: integer("end_seconds").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  engagementScore: numeric("engagement_score", { precision: 8, scale: 4 }),
  clipUrl: text("clip_url"),
  status: text("status").notNull().default("pending"), // HighlightStatus
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_webinar_hl_tenant").on(table.tenantId),
  index("idx_webinar_hl_webinar").on(table.tenantId, table.webinarId),
  index("idx_webinar_hl_status").on(table.tenantId, table.webinarId, table.status),
]);
